import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { createPortal } from 'react-dom';
import { layoutViewportHeight } from '@/lib/viewport';

export interface SelectOption {
    value: string;
    label: string;
    color?: string;
}

/** First option index after `startAfterIndex` whose label's first character matches `key` (case-insensitive). Wraps; use -1 to search from the start. */
function findNextLetterMatchIndex(
    options: SelectOption[],
    key: string,
    startAfterIndex: number
): number {
    const lower = key.toLowerCase();
    const n = options.length;
    if (n === 0) return -1;
    for (let i = 1; i <= n; i++) {
        const idx = (startAfterIndex + i + n) % n;
        const label = options[idx].label.trim();
        if (label.length === 0) continue;
        if (label.charAt(0).toLowerCase() === lower) {
            return idx;
        }
    }
    return -1;
}

interface CustomSelectProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    /** 'ghost' removes border, background and padding — use when embedding inside a custom card. */
    variant?: 'default' | 'ghost';
    // Optional: render a custom display for the selected value
    renderValue?: (option: SelectOption | undefined) => React.ReactNode;
    // Optional: render custom option content
    renderOption?: (option: SelectOption, isSelected: boolean) => React.ReactNode;
    autoOpen?: boolean;
    shouldCloseOnSelect?: (value: string) => boolean;
    footerAction?: {
        label: string;
        onClick: () => void;
        /** Close the dropdown on click, e.g. when the action opens a modal. Default: stays open. */
        closes?: boolean;
    };
}

export function CustomSelect({
    value,
    onChange,
    options,
    placeholder = 'Select...',
    disabled = false,
    className = '',
    variant = 'default',
    renderValue,
    renderOption,
    autoOpen = false,
    shouldCloseOnSelect,
    footerAction,
}: CustomSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const lastTypeaheadRef = useRef<{ key: string; time: number }>({ key: '', time: 0 });
    const touchStartYRef = useRef<number | null>(null);
    const openRemeasureTimersRef = useRef<number[]>([]);
    const [dropdownPosition, setDropdownPosition] = useState<{
        top: number;
        left: number;
        width: number;
        maxHeight: number;
        openUpward: boolean;
    } | null>(null);

    const clearOpenRemeasureTimers = useCallback(() => {
        for (const id of openRemeasureTimersRef.current) {
            clearTimeout(id);
        }
        openRemeasureTimersRef.current = [];
    }, []);

    // Keep track of latest state for keyboard handlers without reattaching listeners
    const stateRef = useRef({ activeIndex, options, isOpen });
    useEffect(() => {
        stateRef.current = { activeIndex, options, isOpen };
    }, [activeIndex, options, isOpen]);

    // Reset activeIndex when opened
    useEffect(() => {
        if (isOpen) {
            const currentIndex = options.findIndex(opt => opt.value === value);
            setActiveIndex(currentIndex >= 0 ? currentIndex : 0);
            lastTypeaheadRef.current = { key: '', time: 0 };
        } else {
            setActiveIndex(-1);
            clearOpenRemeasureTimers();
        }
    }, [isOpen, value, options, clearOpenRemeasureTimers]);

    const selectedOption = options.find(opt => opt.value === value);

    // Calculate dropdown position when opening
    const calculatePosition = useCallback(() => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        // Prefer visual viewport: window.innerHeight stays full-screen on many mobile
        // browsers while the keyboard is open, which makes "open upward" fire wrongly.
        const vv = window.visualViewport;
        const viewportHeight = vv ? layoutViewportHeight(vv) : window.innerHeight;
        const viewportTop = vv?.offsetTop ?? 0;
        const spaceBelow = viewportTop + viewportHeight - rect.bottom;
        const spaceAbove = rect.top - viewportTop;
        const dropdownMaxHeight = 240; // max-h-60 in pixels (15rem)
        const minSpaceNeeded = 100; // Minimum space needed to open downward

        // Only open upward if there's very little space below AND more space above
        // Prefer opening downward in most cases
        const openUpward = spaceBelow < minSpaceNeeded && spaceAbove > spaceBelow;

        // Calculate the actual max height based on available space
        const availableHeight = openUpward ? spaceAbove - 8 : spaceBelow - 8;
        const actualMaxHeight = Math.min(dropdownMaxHeight, Math.max(availableHeight, minSpaceNeeded));

        setDropdownPosition({
            top: openUpward ? rect.top : rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            maxHeight: actualMaxHeight,
            openUpward,
        });
    }, []);

    // Blur any focused text field so the keyboard dismisses before we measure.
    // Remeasure a few times while the viewport settles (keyboard animation).
    const openDropdown = useCallback(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== triggerRef.current) {
            active.blur();
        }

        clearOpenRemeasureTimers();
        calculatePosition();
        setIsOpen(true);

        requestAnimationFrame(calculatePosition);
        openRemeasureTimersRef.current = [50, 150, 300].map((ms) =>
            window.setTimeout(calculatePosition, ms)
        );
    }, [calculatePosition, clearOpenRemeasureTimers]);

    useEffect(() => () => clearOpenRemeasureTimers(), [clearOpenRemeasureTimers]);

    // Handle autoOpen
    useEffect(() => {
        if (autoOpen && !disabled) {
            // Need a slight delay to ensure layout is done and triggerRef is available
            const timer = setTimeout(() => {
                openDropdown();
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [autoOpen, disabled, openDropdown]);

    // Handle opening the dropdown
    const handleToggle = useCallback(() => {
        if (disabled) return;

        if (isOpen) {
            setIsOpen(false);
        } else {
            openDropdown();
        }
    }, [disabled, isOpen, openDropdown]);

    // Handle selecting an option
    const handleSelect = useCallback((optionValue: string) => {
        onChange(optionValue);
        if (shouldCloseOnSelect?.(optionValue) === false) {
            return;
        }

        setIsOpen(false);
    }, [onChange, shouldCloseOnSelect]);
    // Read through a ref so the listeners below don't re-attach whenever the parent passes a new
    // onChange: a re-render during a pointerdown would detach the listener before the event
    // reaches document, and the outside click would be missed.
    const handleSelectRef = useRef(handleSelect);
    handleSelectRef.current = handleSelect;

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                triggerRef.current &&
                !triggerRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };

        const scrollToIndex = (index: number) => {
            setTimeout(() => {
                if (!dropdownRef.current) return;
                const optionNode = dropdownRef.current.children[index] as HTMLElement;
                if (optionNode && optionNode.scrollIntoView) {
                    optionNode.scrollIntoView({ block: 'nearest' });
                }
            }, 0);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            const { activeIndex: currentActive, options: currentOptions } = stateRef.current;
            
            if (event.key === 'Escape') {
                setIsOpen(false);
                triggerRef.current?.focus();
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                setActiveIndex(prev => {
                    const next = prev < currentOptions.length - 1 ? prev + 1 : prev;
                    scrollToIndex(next);
                    return next;
                });
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                setActiveIndex(prev => {
                    const next = prev > 0 ? prev - 1 : prev;
                    scrollToIndex(next);
                    return next;
                });
            } else if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                if (currentActive >= 0 && currentActive < currentOptions.length) {
                    handleSelectRef.current(currentOptions[currentActive].value);
                    triggerRef.current?.focus();
                } else {
                    setIsOpen(false);
                    triggerRef.current?.focus();
                }
            } else if (event.key === 'Tab') {
                if (currentActive >= 0 && currentActive < currentOptions.length) {
                    handleSelectRef.current(currentOptions[currentActive].value);
                } else {
                    setIsOpen(false);
                }
            } else if (
                event.key.length === 1 &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey
            ) {
                const char = event.key;
                const now = Date.now();
                const { key: prevKey, time: prevTime } = lastTypeaheadRef.current;
                const repeatWindowMs = 800;
                const repeatSame =
                    prevKey === char && now - prevTime < repeatWindowMs;
                lastTypeaheadRef.current = { key: char, time: now };

                const startAfter = repeatSame ? currentActive : -1;
                const matchIndex = findNextLetterMatchIndex(currentOptions, char, startAfter);
                if (matchIndex >= 0) {
                    event.preventDefault();
                    event.stopPropagation();
                    setActiveIndex(matchIndex);
                    scrollToIndex(matchIndex);
                }
            }
        };

        // Use a small delay to prevent immediate closing.
        // pointerdown is used instead of mousedown because iOS Safari does not
        // fire mousedown on non-interactive elements.
        const timeoutId = setTimeout(() => {
            document.addEventListener('pointerdown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }, 10);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('pointerdown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    // Recalculate position on scroll/resize/keyboard (visualViewport)
    useEffect(() => {
        if (!isOpen) return;

        window.addEventListener('scroll', calculatePosition, true);
        window.addEventListener('resize', calculatePosition);
        const vv = window.visualViewport;
        vv?.addEventListener('resize', calculatePosition);
        vv?.addEventListener('scroll', calculatePosition);

        return () => {
            window.removeEventListener('scroll', calculatePosition, true);
            window.removeEventListener('resize', calculatePosition);
            vv?.removeEventListener('resize', calculatePosition);
            vv?.removeEventListener('scroll', calculatePosition);
        };
    }, [isOpen, calculatePosition]);

    // Render the trigger button
    const renderTrigger = () => {
        const displayContent = renderValue
            ? renderValue(selectedOption)
            : selectedOption?.label || placeholder;

        return (
            <button
                ref={triggerRef}
                type="button"
                onClick={handleToggle}
                // ponytail: no touchend+preventDefault — that kept the prior text field focused,
                // so the keyboard bounced back and fixed-menu coords went to the top-left.
                style={{ touchAction: 'manipulation' }}
                onKeyDown={(e) => {
                    if (!isOpen && !disabled && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                        e.preventDefault();
                        e.stopPropagation();
                        openDropdown();
                    }
                }}
                disabled={disabled}
                className={variant === 'ghost'
                    ? `w-full flex items-center justify-between gap-1 text-sm text-left transition-colors duration-150 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${className}`
                    : `w-full flex items-center justify-between gap-2 p-2 border rounded-lg text-sm text-left transition-colors duration-150 ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : 'bg-white border-gray-300 hover:border-gray-400 cursor-pointer'} ${isOpen ? 'border-blue-500 ring-2 ring-blue-100' : ''} ${className}`
                }
            >
                <span className={`flex-1 min-w-0 ${renderValue ? 'overflow-hidden' : 'truncate'} ${!selectedOption ? 'text-gray-400' : 'text-gray-900'}`}>
                    {displayContent}
                </span>
                <ChevronDown
                    className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>
        );
    };

    // Render the dropdown menu
    const renderDropdown = () => {
        if (!isOpen || !dropdownPosition) return null;

        const menuWidth = Math.max(dropdownPosition.width, 240);
        const rawTop = dropdownPosition.openUpward
            ? dropdownPosition.top - dropdownPosition.maxHeight - 4
            : dropdownPosition.top;
        const vv = window.visualViewport;
        const viewportLeft = vv?.offsetLeft ?? 0;
        const viewportWidth = vv ? vv.width * (vv.scale || 1) : window.innerWidth;
        const clampedLeft = Math.min(
            Math.max(dropdownPosition.left, viewportLeft + 8),
            viewportLeft + viewportWidth - menuWidth - 8
        );

        const dropdownStyle: React.CSSProperties = {
            position: 'fixed',
            top: Math.max(8, rawTop),
            left: clampedLeft,
            width: menuWidth,
            maxHeight: dropdownPosition.maxHeight,
            zIndex: 9999,
        };

        return createPortal(
            <div
                ref={dropdownRef}
                style={dropdownStyle}
                className={`
                    bg-white border border-gray-200 rounded-lg shadow-lg
                    overflow-hidden
                    animate-in fade-in-0 zoom-in-95 duration-150
                `}
            >
                <div
                    className="overflow-auto overscroll-contain"
                    style={{ maxHeight: footerAction ? Math.max(dropdownPosition.maxHeight - 44, 60) : dropdownPosition.maxHeight }}
                >
                    {options.length === 0 ? (
                        <div className="p-3 text-sm text-gray-400 text-center">
                            No options available
                        </div>
                    ) : (
                        options.map((option, index) => {
                            const isSelected = option.value === value;
                            const isActive = index === activeIndex;

                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    onTouchStart={(e) => { touchStartYRef.current = e.touches[0].clientY; }}
                                    onTouchEnd={(e) => {
                                        const startY = touchStartYRef.current;
                                        const endY = e.changedTouches[0].clientY;
                                        if (startY !== null && Math.abs(endY - startY) > 10) return;
                                        e.preventDefault();
                                        handleSelect(option.value);
                                    }}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    style={{ touchAction: 'manipulation' }}
                                    className={`
                                        w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left
                                        transition-colors duration-100
                                        ${isSelected
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                                        }
                                        ${isActive && !isSelected ? 'bg-gray-100 text-gray-900' : ''}
                                        ${isActive && isSelected ? 'bg-blue-100' : ''}
                                    `}
                                >
                                    {renderOption ? (
                                        renderOption(option, isSelected)
                                    ) : (
                                        <>
                                            {option.color && (
                                                <span
                                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: option.color }}
                                                />
                                            )}
                                            <span className="flex-1 truncate">{option.label}</span>
                                            {isSelected && (
                                                <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                            )}
                                        </>
                                    )}
                                </button>
                            );
                        })
                    )}
                </div>
                {footerAction && (
                    <button
                        type="button"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            footerAction.onClick();
                            if (footerAction.closes) {
                                setIsOpen(false);
                                return;
                            }
                            window.requestAnimationFrame(calculatePosition);
                        }}
                        style={{ touchAction: 'manipulation' }}
                        className="w-full border-t border-gray-200 px-3 py-2.5 text-left text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors whitespace-normal break-words leading-snug"
                    >
                        {footerAction.label}
                    </button>
                )}
            </div>,
            document.body
        );
    };

    return (
        <>
            {renderTrigger()}
            {renderDropdown()}
        </>
    );
}
